import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ServiceBase } from '../infrastructure/service-base';
import { Rights } from './auth.service';
import { DataObject } from './backend-caller.service';
import { BackendService } from './backend.service';
import { map } from 'rxjs/operators';

export interface Record {
  id?: string;
}

export type OutputFormat = 'json' | 'formData';

@Injectable({
  providedIn: 'root'
})
export abstract class ServiceCRUD<Manageable extends Record, Readable extends Record,
  RawManageable = Manageable, RawReadable = Readable> extends ServiceBase {
  protected outputFormat: OutputFormat = 'json';

  public abstract get object(): keyof Rights;

  protected abstract convertRawToProcessed(rawRecord: RawReadable): Readable;

  public abstract readableToManageable(record: Readable): Manageable;

  protected abstract convertProcessedToRaw(record: Manageable): RawManageable;

  public constructor(
    backend: BackendService
  ) {
    super(backend);
  }

  public list(params?: DataObject, extended?: boolean): Observable<Readable[]> {
    const method = extended ? 'extended_list' : '';
    return this.makeGetCall(method, params)
      .pipe(map<RawReadable[], Readable[]>(records => {
        return records.map(this.convertRawToProcessed);
      }));
  }

  public create(record: Manageable): Observable<Readable> {
    return this.makePostCall('', this.convert(record))
      .pipe(map<RawReadable, Readable>(this.convertRawToProcessed));
  }

  public read(id: string): Observable<Readable> {
    return this.makeGetCall(id)
      .pipe(map<RawReadable, Readable>(this.convertRawToProcessed));
  }

  public update(record: Manageable): Observable<Readable> {
    return this.makePutCall(record.id, this.convert(record))
      .pipe(map<RawReadable, Readable>(this.convertRawToProcessed));
  }

  public remove(id: string): Observable<boolean> {
    return this.makeDeleteCall(id);
  }

  private convert(record: Manageable): RawManageable | FormData {
    const recordRaw: RawManageable = this.convertProcessedToRaw(record);

    switch (this.outputFormat) {
      case 'formData':
        return this.jsonToFormData(recordRaw);

      default:
        return recordRaw;
    }
  }

  private jsonToFormData(record: RawManageable): FormData {
    const formData = new FormData();

    for (const fieldName of Object.keys(record)) {
      const fieldValue = record[fieldName];

      if (fieldValue instanceof File) {
        formData.append(fieldName, fieldValue, fieldValue.name);
      } else if (Array.isArray(fieldValue)) {
        formData.append(fieldName, fieldValue.join(' '));
      } else if (fieldValue !== undefined) {
        formData.append(fieldName, fieldValue === null ? '' : String(fieldValue));
      }
    }

    return formData;
  }
}
